<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { ElInput, ElOption, ElSelect } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import { useDebounceFn } from '@vueuse/core'
import VisitDetails from '~/components/VisitDetails.vue'
import VisitsList from '~/components/VisitsList.vue'
import { useVisitsStore } from '~/stores/visits'
import { useLocationsStore } from '~/stores/locations'

useHead({
  title: 'Dray Dog - Tracking',
})

// Map style state
const mapStyle = ref<'streets' | 'satellite'>('streets')

// Filter states
const externalDeviceIds = ref('')
const selectedLocationIds = ref<string[]>([])

// Get stores
const visitsStore = useVisitsStore()
const locationsStore = useLocationsStore()
const { locationOptions, loading: loadingLocations } =
  storeToRefs(locationsStore)

// Load locations on mount
onMounted(() => {
  locationsStore.loadLocations()
})

// Debounced filter update
const updateFilters = useDebounceFn(() => {
  visitsStore.setFilters({
    externalDeviceIds: externalDeviceIds.value || undefined,
    locationIds: selectedLocationIds.value.join(',') || undefined,
  })
  visitsStore.loadMore()
}, 500)

// Handle filter changes
const handleExternalDeviceIdsChange = (value: string) => {
  externalDeviceIds.value = value
  updateFilters()
}

const handleLocationChange = (value: string[]) => {
  selectedLocationIds.value = value
  updateFilters()
}
</script>

<template>
  <div class="flex flex-col h-[calc(100vh-9rem)] !p-0 gap-4">
    <!-- Filters Section -->
    <div class="bg-white p-4 rounded-md shadow-sm border border-gray-200">
      <div class="flex items-center gap-4">
        <ElInput
          v-model="externalDeviceIds"
          placeholder="Search by truck ID (comma-separated)"
          class="w-72"
          clearable
          @update:model-value="handleExternalDeviceIdsChange"
        >
          <template #prefix>
            <ElIcon><Search /></ElIcon>
          </template>
        </ElInput>

        <ElSelect
          v-model="selectedLocationIds"
          placeholder="Filter by terminal"
          class="w-72"
          multiple
          clearable
          :loading="loadingLocations"
          @change="handleLocationChange"
        >
          <ElOption
            v-for="option in locationOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </ElSelect>
      </div>
    </div>

    <!-- Main Content -->
    <div class="grid grid-cols-12 gap-4 h-full flex-1">
      <!-- Left Column - Visits List -->
      <div
        class="col-span-7 overflow-hidden flex flex-col border border-gray-200 rounded-md shadow-sm"
      >
        <div class="flex-1 overflow-auto">
          <VisitsList />
        </div>
      </div>

      <!-- Right Column -->
      <div class="col-span-5 overflow-hidden flex flex-col">
        <VisitDetails v-model="mapStyle" />
      </div>
    </div>
  </div>
</template>

<style>
/* Custom scrollbar styles */
.overflow-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(203, 213, 225, 0.4) transparent;
}

.overflow-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background-color: rgba(203, 213, 225, 0.4);
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.6);
}

:deep(.el-input-number .el-input__wrapper) {
  padding-left: 0;
}

:deep(.el-select .el-input__wrapper) {
  box-shadow: 0 0 0 1px #e5e7eb;
}

:deep(.el-select .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #d1d5db;
}

:deep(.el-input .el-input__wrapper) {
  box-shadow: 0 0 0 1px #e5e7eb;
}

:deep(.el-input .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #d1d5db;
}
</style>

<route lang="yaml">
meta:
  auth: true
name: tracking
</route>
